import type { SceneDirector } from "./SceneDirector";
import type { VignettePlayer } from "./VignettePlayer";
import type { DayClock } from "./DayClock";
import type { LongArcState } from "./LongArcState";

/**
 * Ticks periodically; whenever Johnny is idle (VignettePlayer isn't
 * mid-playback) and a randomized cooldown since the last check has
 * elapsed, asks SceneDirector for the next vignette and hands it to
 * VignettePlayer. The cooldown is randomized per-cycle (not fixed) so
 * vignette pacing doesn't feel metronomic.
 */
export class Scheduler {
  private readonly director: SceneDirector;
  private readonly player: VignettePlayer;
  private readonly dayClock: DayClock;
  private readonly longArcState: LongArcState;
  private readonly minCooldownMs: number;
  private readonly maxCooldownMs: number;
  private cooldownRemainingMs: number;

  constructor(
    director: SceneDirector,
    player: VignettePlayer,
    dayClock: DayClock,
    longArcState: LongArcState,
    minCooldownMs = 4_000,
    maxCooldownMs = 9_000
  ) {
    this.director = director;
    this.player = player;
    this.dayClock = dayClock;
    this.longArcState = longArcState;
    this.minCooldownMs = minCooldownMs;
    this.maxCooldownMs = maxCooldownMs;
    this.cooldownRemainingMs = this.randomCooldown();
  }

  update(deltaMs: number): void {
    if (this.player.isPlaying()) return;

    this.cooldownRemainingMs -= deltaMs;
    if (this.cooldownRemainingMs > 0) return;
    this.cooldownRemainingMs = this.randomCooldown();

    const vignette = this.director.pickNext(this.dayClock.getTimeOfDay(), this.longArcState.getSimDayCount());
    if (vignette) this.player.play(vignette);
  }

  private randomCooldown(): number {
    return this.minCooldownMs + Math.random() * (this.maxCooldownMs - this.minCooldownMs);
  }
}
